import { useState } from 'react';
import { useSiteSettings, SiteSettings } from '../../../contexts/SiteSettingsContext';

const COLOR_PRESETS = [
  { name: 'Teal', hex: '#0d9488' },
  { name: 'Emerald', hex: '#059669' },
  { name: 'Forest', hex: '#16a34a' },
  { name: 'Sage', hex: '#65a30d' },
  { name: 'Cyan', hex: '#0891b2' },
  { name: 'Orange', hex: '#ea580c' },
  { name: 'Rose', hex: '#e11d48' },
  { name: 'Slate', hex: '#475569' },
];

export default function AdminSiteSettingsTab() {
  const { settings, saveSettings } = useSiteSettings();
  const [form, setForm] = useState<SiteSettings>({ ...settings });
  const [saved, setSaved] = useState(false);

  const set = (k: keyof SiteSettings, v: string) => {
    setForm((p) => ({ ...p, [k]: v }));
    setSaved(false);
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    saveSettings(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const inputCls =
    'w-full bg-[#1a3333] border border-teal-900/40 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-700 focus:border-teal-600 transition-colors';

  return (
    <form onSubmit={handleSave} className="space-y-8 max-w-3xl">
      {/* Color */}
      <div className="bg-[#0d2020] border border-white/5 rounded-2xl p-6">
        <h3 className="font-semibold text-teal-300 text-sm uppercase tracking-wider mb-2">Primary Brand Color</h3>
        <p className="text-teal-600 text-xs mb-6">
          This color applies to buttons, accents, and highlights across the entire public website.
        </p>

        {/* Preview bar */}
        <div
          className="h-10 rounded-xl mb-6 flex items-center justify-center text-white text-sm font-semibold"
          style={{ background: form.primaryColor }}
        >
          Preview — {form.primaryColor}
        </div>

        {/* Presets */}
        <div className="grid grid-cols-4 md:grid-cols-8 gap-3 mb-5">
          {COLOR_PRESETS.map(({ name, hex }) => (
            <button
              key={hex}
              type="button"
              onClick={() => set('primaryColor', hex)}
              className="flex flex-col items-center gap-1.5 cursor-pointer group"
            >
              <div
                className="w-10 h-10 rounded-xl border-2 transition-all"
                style={{
                  background: hex,
                  borderColor: form.primaryColor === hex ? 'white' : 'transparent',
                  boxShadow: form.primaryColor === hex ? `0 0 0 3px ${hex}40` : 'none',
                }}
              />
              <span className="text-teal-500 text-xs group-hover:text-teal-300 transition-colors">{name}</span>
            </button>
          ))}
        </div>

        {/* Custom hex */}
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex-shrink-0 border border-teal-900/40"
            style={{ background: form.primaryColor }}
          />
          <input
            type="text"
            value={form.primaryColor}
            onChange={(e) => set('primaryColor', e.target.value)}
            placeholder="#0d9488"
            className="flex-1 bg-[#1a3333] border border-teal-900/40 rounded-xl px-4 py-2.5 text-sm text-white outline-none font-mono placeholder-teal-700"
          />
          <input
            type="color"
            value={form.primaryColor}
            onChange={(e) => set('primaryColor', e.target.value)}
            className="w-10 h-10 rounded-lg border border-teal-900/40 cursor-pointer bg-transparent"
            title="Pick a custom color"
          />
        </div>
      </div>

      {/* Company Info */}
      <div className="bg-[#0d2020] border border-white/5 rounded-2xl p-6">
        <h3 className="font-semibold text-teal-300 text-sm uppercase tracking-wider mb-2">Company Information</h3>
        <p className="text-teal-600 text-xs mb-6">
          These details appear in the public navbar, footer, and contact page.
        </p>
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2">Company Name</label>
              <input value={form.companyName} onChange={(e) => set('companyName', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2">Tagline</label>
              <input value={form.companyTagline} onChange={(e) => set('companyTagline', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2">
                <i className="ri-mail-line mr-1" />
                Email Address
              </label>
              <input type="email" value={form.companyEmail} onChange={(e) => set('companyEmail', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2">
                <i className="ri-phone-line mr-1" />
                Phone Number
              </label>
              <input type="tel" value={form.companyPhone} onChange={(e) => set('companyPhone', e.target.value)} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2">
              <i className="ri-map-pin-line mr-1" />
              Office Address
            </label>
            <input value={form.companyAddress} onChange={(e) => set('companyAddress', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2">
              <i className="ri-global-line mr-1" />
              Live Site URL
              <span className="ml-2 text-teal-700 font-normal normal-case">(used in QR codes &amp; email tracking links)</span>
            </label>
            <div className="relative">
              <i className="ri-links-line absolute left-4 top-1/2 -translate-y-1/2 text-teal-600 text-sm pointer-events-none" />
              <input
                type="url"
                value={form.siteUrl}
                onChange={(e) => set('siteUrl', e.target.value)}
                placeholder="https://swift-tracks.com"
                className={inputCls + ' pl-10'}
              />
            </div>
            <p className="text-teal-800 text-xs mt-1.5 flex items-center gap-1">
              <i className="ri-information-line" />
              Must be your real public domain — not a preview URL. QR codes on shipping labels will link here.
            </p>
          </div>
        </div>
      </div>

      {/* Save */}
      <div className="flex items-center gap-4">
        <button
          type="submit"
          className="px-8 py-3.5 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
          style={{ background: 'var(--primary)' }}
        >
          <i className="ri-save-line mr-2" />
          Save & Apply Changes
        </button>
        {saved && (
          <div className="flex items-center gap-2 text-teal-400 text-sm animate-fade_in">
            <i className="ri-checkbox-circle-fill" />
            Settings saved and applied to the public site!
          </div>
        )}
      </div>
    </form>
  );
}
